import React, { useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import Column from '@amzn/meridian/column'
import Row from '@amzn/meridian/row'
import Text from '@amzn/meridian/text'
import Tag from '@amzn/meridian/tag'

import ToolName from '@components/NavBar/ToolName'
import { getSelectedTool } from '@atredux/selectors/toolSelector'
import { getNotificationCount } from '@atredux/selectors/notificationSelector'
import { getStoreList, getNotificationStoreId } from '@atredux/selectors/storeSelector'
import { isSelectedToolViewOnly } from '@atredux/selectors/viewOnlySelector'
import { setNotificationCount } from '@atredux/slices/notificationSlice'
import { NotificationAPIService } from '@src/pages/NotificationsPage/services/NotificationAPIService'
import { calculateTotalNotificationCount } from '@src/pages/NotificationsPage/utils'
import { ActionCenterAPIService } from '@src/pages/ActionCenterPage/services/ActionCenterAPIService'
import { BADGE_MAX_VALUE } from '@src/pages/ActionCenterPage/constants'
import NotificationIcon from '@components/NavBar/NotificationIcon'
import Button from '@amzn/meridian/button'
import Icon from '@amzn/meridian/icon'
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small'
import ProfileIcon from '@components/NavBar/ProfileIcon'
import ProblemSolveIcon from '@components/NavBar/ProblemSolveIcon'
import { isNewHomepageTool } from '@services/ToolMenuConfigService'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { useFeatureFlag } from '@services/featureFlags'
import { useViewOnlySelector } from '@hooks/useViewOnlySelector'

export const VIEW_ONLY_BADGE = 'jsma-web-storeScan-viewOnlyBadge'

interface INavBarInfoProps {
    userEmail: string,
    storeId: string,
    showMenuButton?: boolean
    showNewHomePageFeatures?: boolean
    openExitSheet?: () => void
    toggleProfile?: () => void
    toggleProblemSolve?: () => void
    toggleNotificationsSheet?: () => void
    overrideToolName?: string
    /** True while the header names a shell page (Store Tools, Action Center, Profile), not the tool. */
    isShellPageOpen?: boolean
}

/**
 * Controlled component that renders tool name and navigation icons including
 * profile, problem solve, inventory viewer, assisted shopping, and notifications.
 *
 * @param {boolean} showNewHomePageFeatures - Enables new homepage features
 * @param {boolean} showMenuButton - Controls menu button visibility
 * @param {Function} openExitSheet - Handler for exit button
 * @param {Function} toggleProfile - Handler for profile icon
 * @param {Function} toggleProblemSolve - Handler for problem solve icon
 * @param {Function} toggleNotificationsSheet - Handler for notifications icon
 * @param {string} overrideToolName - Optional tool name override
 * @param {boolean} isShellPageOpen - Hides the tool's view-only badge while a shell page is up
 * @returns {React.ReactElement}
 */
const NavBarInfo = ({
    userEmail,
    showNewHomePageFeatures,
    showMenuButton,
    openExitSheet,
    toggleProfile,
    toggleProblemSolve,
    toggleNotificationsSheet,
    overrideToolName,
    isShellPageOpen,
}: Readonly<INavBarInfoProps>): React.ReactElement => {
    const { enabled: profileEnabled } = useFeatureFlag('profile_settings_v1')
    const { enabled: actionCenterEnabled } = useFeatureFlag('golden_gate_in_app_notification_page_v2')

    const [bundle] = useBundle(BUNDLE_NAME)
    const dispatch = useDispatch()
    const tool = useSelector(getSelectedTool)
    const notificationCount = useSelector(getNotificationCount)
    const notificationStoreId = useSelector(getNotificationStoreId)
    const storeList = useSelector(getStoreList)
    const isViewOnly = useViewOnlySelector(isSelectedToolViewOnly)

    const otherStoreIds = useMemo(
        () => storeList.map((s) => s.storeId).filter((id) => id && id !== notificationStoreId),
        [storeList, notificationStoreId],
    )

    // Legacy centre: per-store fan-out, so the badge depends on which stores are in play.
    // Note: no storeListLoaded guard here — otherStoreIds may initially be empty,
    // causing a first fetch for just the primary store. When the store list loads,
    // otherStoreIds updates and triggers a second fetch with the full set.
    // This is preferable to blocking the badge entirely due to race conditions.
    useEffect(() => {
        if (actionCenterEnabled) return

        if (!notificationStoreId) {
            dispatch(setNotificationCount(0))
            return
        }

        let isMounted = true

        const fetchNotifications = async () => {
            try {
                const count = calculateTotalNotificationCount(await NotificationAPIService.getNotifications(notificationStoreId, otherStoreIds))
                if (isMounted) {
                    dispatch(setNotificationCount(count))
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching notifications', error)
                    dispatch(setNotificationCount(0))
                }
            }
        }

        fetchNotifications()

        return () => {
            isMounted = false
        }
    }, [notificationStoreId, otherStoreIds, dispatch, actionCenterEnabled])

    // Action Center: the inbox is keyed on the caller, not the store, so the read is the same
    // whichever store is current and is deliberately not re-run on store changes. One page is
    // enough for a badge: an exact count when it fits, "50+" (BADGE_MAX_VALUE) when it does not.
    // Opening the sheet drains the inbox and publishes the exact total over this.
    useEffect(() => {
        if (!actionCenterEnabled) return

        let isMounted = true

        ActionCenterAPIService.countUnreadNotifications()
            .then(({ count, hasMore }) => {
                if (isMounted) {
                    dispatch(setNotificationCount(hasMore ? BADGE_MAX_VALUE + 1 : count))
                }
            })
            .catch((error) => {
                if (isMounted) {
                    console.error('Error fetching notifications', error)
                    dispatch(setNotificationCount(0))
                }
            })

        return () => {
            isMounted = false
        }
    }, [dispatch, actionCenterEnabled])

    const displayToolName = overrideToolName || tool?.displayName

    return (
        <Row alignmentVertical="center" width="100%">
            {/* Controlling the show/hide close button toggle based on showNewHomePageFeatures is enabled or not */}
            {!showMenuButton && (
                <Button type="icon" id={`closeButton`} data-testid = {'CloseIcon'} onClick={openExitSheet}>
                    <Icon tokens={closeSmallTokens}>{t(bundle, 'jsma-web-common-close')}</Icon>
                </Button>
            )}
            <div style={{ overflow: 'hidden', flex: 1, minWidth: 0 }}><Column spacing="xxsmall">
                {/* `widths` is positional, and only the tool name -- always the first child --
                    fills. The icons keep their natural width and are pushed flush right. */}
                <Row alignmentVertical="center" widths={['fill']}>
                    {/* The name and the view-only badge share the filling slot, so the badge sits
                        right after the name instead of being pushed out beside the icons. */}
                    <Row alignmentVertical="center" spacing="small" minWidth={0}>
                        <ToolName toolName={displayToolName} />
                        {isViewOnly && !isShellPageOpen && (
                            <div className="navbar-view-only-tag">
                                <Tag type="theme" data-atb-id="view-only-tag">
                                    {t(bundle, VIEW_ONLY_BADGE) || ''}
                                </Tag>
                            </div>
                        )}
                    </Row>
                    {showNewHomePageFeatures && !isNewHomepageTool(tool) && (
                        <ProblemSolveIcon toggleProblemSolve={toggleProblemSolve}/>
                    )}
                    {(!showNewHomePageFeatures || (showNewHomePageFeatures && isNewHomepageTool(tool))) && (
                        <>
                            <NotificationIcon
                                notificationCount={notificationCount}
                                maxValue={actionCenterEnabled ? BADGE_MAX_VALUE : undefined}
                                storeId={notificationStoreId}
                                requireStore={!actionCenterEnabled}
                                toggleNotificationsSheet={toggleNotificationsSheet}
                            />
                        </>
                    )}
                    {profileEnabled && (
                        <ProfileIcon toggleProfile={toggleProfile}/>
                    )}
                </Row>
                {userEmail && (
                    <Text data-testid="navbar-user-email" type="b200" color="secondary">{userEmail}</Text>
                )}
            </Column></div>
        </Row>
    )
}

export default NavBarInfo
